/**
 * Batch Data tab (DATA_VISUALIZATION_FRONTEND_PLAN §2.2).
 *
 * The same series chart + table as the Explorer, but auto-scoped to the
 * batch's period (read-only range — no picker; an "Open in Explorer" link
 * carries the window out for ad-hoc extension). It opens on the hub outlet
 * meter (`DEFAULT_SELECTION`); everything else is addable via the shared picker.
 *
 * The reconciliation strip compares Σ-daily energy against the calc engine's
 * period figures and reads its OWN series, not the charted ones, so changing
 * what is plotted cannot blank it.
 */
import {
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconAlertTriangle, IconExternalLink, IconRefresh } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  indexCalcResultsByKind,
  useBatchCalcResults,
  useBatchCorrections,
  useDataSeriesMany,
  useDataSources,
  useUploads,
} from "../../api/hooks";
import type { BatchDetail, SeriesResponse } from "../../api/types";
import { CalcOverlayLegend, type ReconRow } from "../../components/data/CalcOverlayLegend";
import { CorrectionModeControl } from "../../components/data/CorrectionModeControl";
import { SeriesChart } from "../../components/data/SeriesChart";
import { SeriesTable } from "../../components/data/SeriesTable";
import { SourcePicker } from "../../components/data/SourcePicker";
import { UploadRowsDrawer } from "../../components/data/UploadRowsDrawer";
import { colorForIndex } from "../../components/data/colors";
import {
  applyApprovedOverlay,
  buildPageOverlays,
  parseCorrMode,
} from "../../components/data/correctionOverlay";
import {
  CUSTODY_FILING_ENERGY_ROLE,
  DEFAULT_SELECTION,
  RECONCILIATION_SERIES,
} from "../../components/data/filingSources";
import {
  type Selection,
  decodeSelections,
  encodeSelections,
  selectionKey,
  selectionLabel,
  selectionsToParams,
} from "../../components/data/selection";
import { type ChartSeriesInput, toSeriesData } from "../../components/data/series-data";
import { canAcceptUpload } from "../../lib/batchState";
import { addDaysIso, isoToGasDay } from "../../lib/dateRange";

export function DataTab({ batch }: { batch: BatchDetail }) {
  const catalogQ = useDataSources();
  const calcQ = useBatchCalcResults(batch.batch_id);
  // Corrections overlay source — batch-scoped (this tab has batch context; the
  // Explorer uses the window-scoped GET /data/corrections instead). Usually
  // already warm from the Corrections tab's own query.
  const correctionsQ = useBatchCorrections(batch.batch_id);
  // Uploads list by reporting period (YYYY-MM), not batch_id (BE isn't
  // batch-scoped until accept).
  const uploadsQ = useUploads(batch.period_start.slice(0, 7));

  // period_end is the EXCLUSIVE next-period boundary; the inclusive last
  // gas-day is the day before it.
  const range = useMemo(
    () => ({
      start: isoToGasDay(batch.period_start),
      end: addDaysIso(isoToGasDay(batch.period_end), -1),
    }),
    [batch.period_start, batch.period_end],
  );

  // Source selection is a filter, so it lives in the URL (locked decision: URL
  // is state of record) — reload / back-forward / share reproduces the exact
  // view instead of snapping back to the preselect.
  const [params, setParams] = useSearchParams();
  const selections = useMemo<Selection[]>(() => {
    const raw = params.get("sources");
    return raw !== null ? decodeSelections(raw) : DEFAULT_SELECTION;
  }, [params]);
  const setSelections = (next: Selection[]) => {
    const p = new URLSearchParams(params);
    p.set("sources", next.length === 0 ? "" : encodeSelections(next));
    setParams(p, { replace: true });
  };

  const [variant, setVariant] = useState<"line" | "bar">("line");
  const [drillUpload, setDrillUpload] = useState<{ id: string; label: string } | null>(null);

  // Granularity (URL state). Hourly is offered only when EVERY selected source
  // supports it (raw-totalizer roles); report-view / utility roles are daily-only,
  // so a mixed selection falls back to daily.
  const hourlyRoles = useMemo(
    () =>
      new Set((catalogQ.data?.sources ?? []).filter((s) => s.hourly_capable).map((s) => s.role)),
    [catalogQ.data],
  );
  const hourlyAvailable = selections.length > 0 && selections.every((s) => hourlyRoles.has(s.role));
  const granularity: "daily" | "hourly" =
    params.get("g") === "hourly" && hourlyAvailable ? "hourly" : "daily";
  const setGranularity = (g: string) => {
    const p = new URLSearchParams(params);
    p.set("g", g);
    setParams(p, { replace: true });
  };

  // Corrections display mode (URL state, like granularity): db | approved | all.
  const corrMode = parseCorrMode(params.get("corr"));
  const setCorrMode = (m: string) => {
    const p = new URLSearchParams(params);
    p.set("corr", m);
    setParams(p, { replace: true });
  };

  // No seeding. This used to pin a default set into the `sources` param on first
  // render — four hub series plus the first two dairies on `dairy_net_pipeline`,
  // a role that no longer exists. Writing defaults into the URL also made them
  // sticky: a user who cleared the chart got them back on the next visit, since
  // an empty selection and an absent param were indistinguishable afterwards.
  // The default now lives only in `DEFAULT_SELECTION`, read when `sources` is
  // absent, and the URL records the user's own choice and nothing else.

  const seriesQueries = useDataSeriesMany(
    selectionsToParams(selections, range.start, range.end, granularity),
  );

  // Always daily, and always these two, regardless of what is charted: the strip
  // says "Σ daily", so reading it off an hourly selection mislabelled the figure.
  // react-query dedupes against the charted pulls when they coincide.
  const reconQueries = useDataSeriesMany(
    selectionsToParams(RECONCILIATION_SERIES, range.start, range.end, "daily"),
  );
  const reconTotals = new Map<string, string>();
  const reconFailed = new Set<string>();
  RECONCILIATION_SERIES.forEach((sel, i) => {
    const q = reconQueries[i];
    if (q?.isError) {
      reconFailed.add(sel.role);
      return;
    }
    const total = q?.data?.total.value;
    if (total !== undefined) reconTotals.set(sel.role, total);
  });

  const loadedSeries: { id: string; label: string; index: number; data: SeriesResponse }[] = [];
  // Per-series errors surface as their own chip (as in the Explorer) — a single
  // failing source must not be silently dropped, which would make the chart +
  // reconciliation strip read as complete when a pull actually failed. Pending
  // is counted separately so a slow/failed load isn't mistaken for the
  // "nothing selected" empty state (BUG-3).
  const seriesErrors: { key: string; label: string; message: string }[] = [];
  let pending = 0;
  selections.forEach((sel, i) => {
    const q = seriesQueries[i];
    if (!q) return;
    if (q.isError) {
      seriesErrors.push({
        key: selectionKey(sel),
        label: selectionLabel(sel, catalogQ.data),
        message: q.error instanceof Error ? q.error.message : "Failed to load",
      });
      return;
    }
    if (q.isPending) {
      pending += 1;
      return;
    }
    if (!q.data) return;
    loadedSeries.push({
      id: selectionKey(sel),
      label: selectionLabel(sel, catalogQ.data),
      index: i,
      data: q.data,
    });
  });

  // Join corrections onto the loaded series — keyed by the RESPONSE's
  // facility_identifier (a hub selection carries facility: null, but the
  // correction's source id was built from the response by pickFromSeries).
  const { index: overlayIndex, overlaysById } = buildPageOverlays(
    correctionsQ.data ?? [],
    granularity,
    loadedSeries.map((r) => ({
      id: r.id,
      facilityIdentifier: r.data.facility_identifier,
      role: r.data.role,
      bucketKeys: r.data.points.map((p) => p.bucket_start),
    })),
  );
  // The table gets the RAW points plus the overlay map (it renders original vs
  // corrected itself); the chart plots the APPROVED effective values via the
  // same merge function (pending never plots — table-only disclosure).
  const tableSeries: ChartSeriesInput[] = loadedSeries.map((r) => ({
    id: r.id,
    label: r.label,
    unit: r.data.unit,
    color: colorForIndex(r.index),
    points: toSeriesData(r.data.points, granularity),
    total: r.data.total,
  }));
  const chartSeries: ChartSeriesInput[] = tableSeries.map((s) => ({
    ...s,
    points: applyApprovedOverlay(s.points, overlaysById.get(s.id), corrMode),
  }));
  const overlayActive = corrMode !== "db" && overlaysById.size > 0;
  const unmatchedCount = corrMode !== "db" ? (overlayIndex?.unmatched.length ?? 0) : 0;

  // Refetch the failed pulls in place — so a transient backend blip recovers
  // with one click instead of forcing the user to remove/re-add a source chip
  // to retrigger the fetch (BUG-3).
  const retryFailed = () => {
    selections.forEach((_, i) => {
      const q = seriesQueries[i];
      if (q?.isError) void q.refetch();
    });
    // The strip's own pulls are not in `selections`, so they need retrying too —
    // otherwise Retry clears the chips and leaves the reconciliation blank.
    for (const q of reconQueries) {
      if (q?.isError) void q.refetch();
    }
  };

  // Reconciliation: Σ-daily energy vs the calc engine's period figures.
  const byKind = indexCalcResultsByKind(calcQ.data ?? []);
  const tokenCalc =
    byKind.get("rfs_token_mmbtu_hhv")?.value ?? batch.totals?.token_mmbtu.value ?? null;
  const utilityCalc =
    byKind.get("rfs_utility_mmbtu_hhv")?.value ?? batch.totals?.utility_mmbtu.value ?? null;

  const reconRows: ReconRow[] = [
    {
      key: "hub_energy",
      label: "Hub inlet energy",
      unit: "mmbtu",
      dailyTotal: reconTotals.get("hub_inlet_energy") ?? null,
      failed: reconFailed.has("hub_inlet_energy"),
      calcValue: tokenCalc,
      calcLabel: "Token MMBtu HHV",
    },
    {
      key: "custody_energy",
      label: "Hub custody energy",
      unit: "mmbtu",
      dailyTotal: reconTotals.get(CUSTODY_FILING_ENERGY_ROLE) ?? null,
      failed: reconFailed.has(CUSTODY_FILING_ENERGY_ROLE),
      calcValue: utilityCalc,
      calcLabel: "Utility MMBtu HHV",
    },
  ];

  const explorerHref = `/data?sources=${encodeURIComponent(
    encodeSelections(selections),
  )}&start=${range.start}&end=${range.end}&g=${granularity}&corr=${corrMode}`;

  // The list is period-scoped (the BE doesn't filter by batch). Narrow to
  // uploads relevant to THIS batch: always those already accepted into it, plus
  // — only when this batch can still accept data — the parse-clean uploads
  // awaiting acceptance. On a locked batch (Approved/Submitted/Frozen) an
  // unlinked pending upload isn't a relevant target, so we don't surface it.
  const batchAcceptsData = canAcceptUpload(batch.state);
  const uploads = (uploadsQ.data?.items ?? []).filter(
    (u) => u.batch_id === batch.batch_id || (batchAcceptsData && u.status === "parsed"),
  );

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              Batch period
            </Text>
            <Text size="sm" c="dimmed" ff="monospace">
              {range.start} → {range.end} (PT gas-days)
            </Text>
          </Stack>
          <Anchor component={Link} to={explorerHref} size="sm">
            <Group gap={4} wrap="nowrap">
              Open in Explorer
              <IconExternalLink size={14} />
            </Group>
          </Anchor>
        </Group>
      </Paper>

      <CalcOverlayLegend rows={reconRows} />

      <Paper p="md" withBorder data-tour="data-chart">
        <Stack gap="md">
          <SourcePicker
            catalog={catalogQ.data}
            value={selections}
            onChange={setSelections}
            loading={catalogQ.isPending}
          />
          {seriesErrors.length > 0 ? (
            <Group gap="xs" wrap="wrap" align="center">
              {seriesErrors.map((e) => (
                <Badge
                  key={e.key}
                  color="red"
                  variant="light"
                  leftSection={<IconAlertTriangle size={12} />}
                >
                  {e.label}: {e.message}
                </Badge>
              ))}
              <Button
                size="compact-xs"
                variant="subtle"
                leftSection={<IconRefresh size={12} />}
                onClick={retryFailed}
              >
                Retry
              </Button>
            </Group>
          ) : null}
          <Group justify="space-between">
            <Group gap="sm">
              <Text size="sm" fw={600}>
                {granularity === "hourly" ? "Hourly series" : "Daily series"}
              </Text>
              {pending > 0 ? (
                <Group gap={6}>
                  <Loader size="xs" />
                  <Text size="xs" c="dimmed">
                    loading {pending}…
                  </Text>
                </Group>
              ) : null}
            </Group>
            <Group gap="sm">
              <Tooltip
                label="Hourly is available only when every selected source supports it. The settled custody energy is one figure per gas day, so a selection including it stays daily."
                disabled={hourlyAvailable}
                multiline
                w={260}
                withArrow
              >
                <SegmentedControl
                  size="xs"
                  value={granularity}
                  onChange={setGranularity}
                  data={[
                    { value: "daily", label: "Daily" },
                    { value: "hourly", label: "Hourly", disabled: !hourlyAvailable },
                  ]}
                />
              </Tooltip>
              <SegmentedControl
                size="xs"
                value={variant}
                onChange={(v) => setVariant(v as "line" | "bar")}
                data={[
                  { value: "line", label: "Line" },
                  { value: "bar", label: "Bar" },
                ]}
              />
              <CorrectionModeControl value={corrMode} onChange={setCorrMode} />
            </Group>
          </Group>
          {chartSeries.length === 0 && pending > 0 ? (
            // Sources selected and loading — don't fall through to SeriesChart's
            // "select one or more sources" message (which reads as a blank chart).
            <Paper p="xl" withBorder>
              <Center mih={260}>
                <Group gap={8}>
                  <Loader size="sm" />
                  <Text c="dimmed" size="sm">
                    Loading daily series…
                  </Text>
                </Group>
              </Center>
            </Paper>
          ) : chartSeries.length === 0 && seriesErrors.length > 0 ? (
            // Sources selected but every pull failed — surface the failure with a
            // retry instead of an empty "select sources" chart (BUG-3).
            <Paper p="xl" withBorder>
              <Center mih={260}>
                <Stack align="center" gap="xs">
                  <IconAlertTriangle size={22} color="var(--mantine-color-red-6)" />
                  <Text c="dimmed" size="sm">
                    Couldn't load the selected series.
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconRefresh size={14} />}
                    onClick={retryFailed}
                  >
                    Retry
                  </Button>
                </Stack>
              </Center>
            </Paper>
          ) : (
            <>
              <SeriesChart
                series={chartSeries}
                variant={variant}
                range={range}
                granularity={granularity}
              />
              {overlayActive ? (
                <Text size="xs" c="dimmed">
                  Chart shows approved-corrected values; pending proposals appear only in the table.
                </Text>
              ) : null}
            </>
          )}
        </Stack>
      </Paper>

      {unmatchedCount > 0 ? (
        <Text size="xs" c="dimmed">
          {unmatchedCount} correction{unmatchedCount === 1 ? "" : "s"} in this window target upload
          rows or free-text sources and aren't shown in this series view.
        </Text>
      ) : null}
      <SeriesTable
        series={tableSeries}
        filename={`batch-${batch.batch_id}_series.csv`}
        range={range}
        granularity={granularity}
        corrections={{ mode: corrMode, overlays: overlaysById }}
      />

      <Paper p="md" withBorder>
        <Text size="sm" fw={600} mb="sm">
          Uploads for this period
        </Text>
        {uploadsQ.isPending ? (
          <Text size="sm" c="dimmed">
            Loading uploads…
          </Text>
        ) : uploads.length === 0 ? (
          <Text size="sm" c="dimmed">
            No uploads recorded for this period yet. When the uploads backend is connected, accepted
            uploads appear here and drill into their parsed rows.
          </Text>
        ) : (
          <Stack gap={6}>
            {uploads.map((u) => (
              <Group
                key={u.id}
                justify="space-between"
                wrap="nowrap"
                onClick={() => setDrillUpload({ id: u.id, label: u.original_filename })}
                style={(t) => ({
                  padding: t.spacing.xs,
                  borderRadius: t.radius.sm,
                  border: `1px solid ${t.colors.gray[2]}`,
                  cursor: "pointer",
                })}
              >
                <Stack gap={0}>
                  <Text size="sm">{u.original_filename}</Text>
                  <Text size="xs" c="dimmed">
                    {u.upload_type} · {u.status}
                  </Text>
                </Stack>
              </Group>
            ))}
          </Stack>
        )}
      </Paper>

      <UploadRowsDrawer
        uploadId={drillUpload?.id ?? null}
        title={drillUpload?.label}
        opened={drillUpload !== null}
        onClose={() => setDrillUpload(null)}
      />
    </Stack>
  );
}
